import { useCallback, useEffect, useState } from 'react';

import { debounce } from 'debounce';

import GoogleAutocomplete from '~/src/common/services/Google/GoogleAutocompleteSuggestion';
import { AutocompleteResult, AutocompleteSessionToken } from '~/src/common/services/Google/types';
import I18n from '~/src/common/services/I18n';

interface Props {
  setError: (error: string) => void;
  setSearching: (loading: boolean) => void;
}

const useSearchAddressPlaces = ({ setError, setSearching }: Props) => {
  const [places, setPlaces] = useState<AutocompleteResult[]>([]);

  // Debounce renvoie une fonction qui doit être stable du fait de l'utilisation de clear
  // Cependant comme les dépendances utilisées dans la fonction de debounce sont cachées pour le hook
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchPlacesDebounced = useCallback(
    debounce(async (inputValue: string, sessionTokenValue: AutocompleteSessionToken) => {
      try {
        if (!/[A-Za-z]/.test(inputValue)) {
          setPlaces([]); // Pas de lettres
        } else if (!/\d/.test(inputValue)) {
          setPlaces([]); // Pas de chiffres
          setError(I18n.t('google-autocomplete.street-number-missing'));
        } else {
          const results = await GoogleAutocomplete.getPlacePredictions(inputValue, {
            types: ['street_address', 'premise', 'subpremise', 'establishment'],
            sessionToken: sessionTokenValue,
          });

          setError('');

          setPlaces(results);
        }
      } catch {
        setPlaces([]);

        setError('');
      } finally {
        setSearching(false);
      }
    }, 1000),
    [setError, setSearching],
  );

  useEffect(() => {
    return () => {
      searchPlacesDebounced.clear();
    };
  }, [searchPlacesDebounced]);

  return {
    places,
    searchPlaces: searchPlacesDebounced,
    clearPlaces: () => setPlaces([]),
  };
};

export default useSearchAddressPlaces;
